import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '@/App';
import { cn } from '@/lib/utils';
import { 
  LayoutDashboard, 
  Users, 
  Building2, 
  Briefcase, 
  FileText, 
  Settings,
  LogOut,
  UserCog
} from 'lucide-react';

const AdminLayout = ({ children }) => {
  const { user, logout } = useAuth();
  const location = useLocation();

  const navItems = [
    { href: '/admin', label: 'Dashboard', icon: LayoutDashboard },
    { href: '/admin/talent', label: 'Talent', icon: Users },
    { href: '/admin/companies', label: 'Companies', icon: Building2 },
    { href: '/admin/projects', label: 'Projects', icon: Briefcase },
    { href: '/admin/applications', label: 'Applications', icon: FileText },
    { href: '/admin/users', label: 'Users', icon: UserCog },
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Sidebar */}
      <aside className="fixed inset-y-0 left-0 z-50 w-64 bg-slate-900 text-white" data-testid="admin-sidebar">
        <div className="flex h-16 items-center px-6 border-b border-slate-800">
          <Link to="/admin" className="flex items-center space-x-2">
            <div className="h-8 w-8 rounded-lg bg-teal-700 flex items-center justify-center">
              <span className="text-white font-bold text-xs">act</span>
            </div>
            <span className="font-bold text-lg">Admin Panel</span>
          </Link>
        </div>

        <nav className="p-4 space-y-1">
          {navItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.href;
            
            return (
              <Link
                key={item.href}
                to={item.href}
                className={cn(
                  'flex items-center space-x-3 px-4 py-3 rounded-lg text-sm font-medium transition-colors',
                  isActive 
                    ? 'bg-primary text-white' 
                    : 'text-slate-400 hover:text-white hover:bg-slate-800'
                )}
                data-testid={`admin-nav-${item.label.toLowerCase()}`}
              >
                <Icon className="h-5 w-5" />
                <span>{item.label}</span>
              </Link>
            );
          })}
        </nav>

        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-slate-800">
          <div className="flex items-center space-x-3 px-4 py-2 mb-2">
            <div className="h-8 w-8 rounded-full bg-slate-700 flex items-center justify-center">
              <span className="text-sm font-medium">{user?.email?.[0]?.toUpperCase()}</span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">{user?.email}</p>
              <p className="text-xs text-slate-400">Administrator</p>
            </div>
          </div>
          <button
            onClick={logout}
            className="flex items-center space-x-3 px-4 py-3 rounded-lg text-sm font-medium text-slate-400 hover:text-white hover:bg-slate-800 w-full transition-colors"
            data-testid="admin-logout"
          >
            <LogOut className="h-5 w-5" />
            <span>Log out</span>
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="pl-64">
        <div className="min-h-screen p-8">
          {children}
        </div>
      </main>
    </div>
  );
};

export default AdminLayout;
